/* Research / Sourcing — ürün listesi sayfası.
 * - Ürün kartına tıklayınca ayrı sayfaya yönlendirir (/research/p/:pid veya /r/:slug/p/:pid)
 * - Geri butonu yok
 * - Bol Satış alanı kaldırıldı
 */
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { safeCopy } from '../utils/clipboard';
import {
    Plus, Search, X, Copy, Lock, Package,
    Loader2, Share2, Upload,
} from 'lucide-react';
import ImportModal from './research/ImportModal';

const API = process.env.REACT_APP_BACKEND_URL;
const fmtEur = (n) => new Intl.NumberFormat('nl-NL', { style: 'currency', currency: 'EUR' }).format(n || 0);

function getHeaders() {
    const slug = sessionStorage.getItem('research_share_slug');
    const token = sessionStorage.getItem('research_share_token');
    if (slug && token) {
        return { 'X-Share-Slug': slug, 'X-Share-Token': token };
    }
    return { Authorization: `Bearer ${localStorage.getItem('token')}` };
}

export default function Research({ shareMode = false }) {
    const navigate = useNavigate();
    const { slug } = useParams();
    const [pinVerified, setPinVerified] = useState(!shareMode);
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(false);
    const [search, setSearch] = useState('');
    const [showAdd, setShowAdd] = useState(false);
    const [showShare, setShowShare] = useState(false);
    const [showImport, setShowImport] = useState(false);

    useEffect(() => {
        if (!shareMode) return;
        const t = sessionStorage.getItem('research_share_token');
        const s = sessionStorage.getItem('research_share_slug');
        if (t && s === slug) setPinVerified(true);
    }, [shareMode, slug]);

    const load = useCallback(async () => {
        if (!pinVerified) return;
        setLoading(true);
        try {
            const r = await axios.get(`${API}/api/research/products`, {
                headers: getHeaders(), params: { search },
            });
            setProducts(r.data?.items || []);
        } catch (e) {
            if (e.response?.status === 401 && shareMode) {
                setPinVerified(false);
                sessionStorage.removeItem('research_share_token');
            } else {
                toast.error(e.response?.data?.detail || 'Yüklenemedi');
            }
        } finally { setLoading(false); }
    }, [search, pinVerified, shareMode]);

    useEffect(() => { load(); }, [load]);

    const openProduct = (p) => {
        const url = shareMode ? `/r/${slug}/p/${p.id}` : `/research/p/${p.id}`;
        navigate(url);
    };

    if (shareMode && !pinVerified) {
        return <PinGate slug={slug} onSuccess={() => setPinVerified(true)} />;
    }

    return (
        <div className="min-h-screen bg-gray-50">
            {/* Header — geri butonu yok */}
            <div className="bg-white border-b border-gray-200 sticky top-0 z-20">
                <div className="max-w-3xl mx-auto px-4 py-3 flex items-center gap-2">
                    <div className="flex-1">
                        <h1 className="text-base font-bold text-gray-900 flex items-center gap-1.5">
                            <Package size={16} /> Ürün Araştırma
                            {shareMode && (
                                <span className="text-[10px] font-semibold px-1.5 py-0.5 bg-indigo-100 text-indigo-700 rounded">SOURCER</span>
                            )}
                        </h1>
                    </div>
                    {!shareMode && (
                        <>
                            <button onClick={() => setShowImport(true)}
                                className="h-8 px-2 text-xs font-bold text-emerald-700 bg-emerald-50 hover:bg-emerald-100 rounded inline-flex items-center gap-1"
                                data-testid="research-import-btn">
                                <Upload size={12} /> AI İçeri Aktar
                            </button>
                            <button onClick={() => setShowShare(true)}
                                className="h-8 px-2 text-xs font-bold text-indigo-700 bg-indigo-50 hover:bg-indigo-100 rounded inline-flex items-center gap-1"
                                data-testid="research-share-btn">
                                <Share2 size={12} /> Paylaş
                            </button>
                        </>
                    )}
                </div>
                <div className="max-w-3xl mx-auto px-4 pb-3">
                    <div className="relative">
                        <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
                        <input value={search} onChange={(e) => setSearch(e.target.value)}
                            placeholder="Ürün ara..."
                            className="w-full h-10 pl-8 pr-3 text-sm border border-gray-300 rounded-md focus:border-indigo-500 outline-none"
                            data-testid="research-search" />
                    </div>
                </div>
            </div>

            {/* Grid */}
            <div className="max-w-3xl mx-auto px-4 py-4">
                {loading ? (
                    <div className="text-center py-12"><Loader2 size={24} className="animate-spin text-indigo-500 inline" /></div>
                ) : products.length === 0 ? (
                    <div className="text-center py-12 text-sm text-gray-500">
                        Henüz ürün yok. Aşağıdaki <strong>+ Ürün Ekle</strong> ile ilk ürünü ekleyin.
                    </div>
                ) : (
                    <div className="bg-white border border-gray-200 rounded-lg overflow-hidden divide-y divide-gray-100">
                        {products.map((p) => (
                            <div key={p.id} onClick={() => openProduct(p)}
                                className="flex items-center gap-3 px-3 py-2 cursor-pointer hover:bg-gray-50 active:bg-gray-100"
                                data-testid={`research-card-${p.id}`}>
                                <div className="w-12 h-12 shrink-0 bg-gray-100 rounded flex items-center justify-center overflow-hidden">
                                    {p.image_url ? (
                                        <img src={p.image_url} alt={p.name} className="w-full h-full object-cover" />
                                    ) : (
                                        <Package size={18} className="text-gray-300" />
                                    )}
                                </div>
                                <div className="flex-1 min-w-0">
                                    <div className="text-sm font-semibold text-gray-900 truncate">{p.name}</div>
                                    <div className="text-[11px] text-gray-500">{p.supplier_count || 0} tedarikçi</div>
                                </div>
                                {p.min_price_eur > 0 && (
                                    <div className="text-sm font-bold text-emerald-700 shrink-0">{fmtEur(p.min_price_eur)}</div>
                                )}
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {/* FAB */}
            <button onClick={() => setShowAdd(true)}
                className="fixed bottom-6 right-6 h-14 w-14 rounded-full bg-indigo-600 hover:bg-indigo-700 text-white shadow-lg flex items-center justify-center z-30"
                data-testid="research-add-fab">
                <Plus size={28} />
            </button>

            {showAdd && <AddProductModal onClose={() => setShowAdd(false)} onSaved={load} />}
            {showShare && <ShareSettings onClose={() => setShowShare(false)} />}
            {showImport && <ImportModal onClose={() => setShowImport(false)} onCommitted={load} />}
        </div>
    );
}

function PinGate({ slug, onSuccess }) {
    const [pin, setPin] = useState('');
    const [busy, setBusy] = useState(false);
    const submit = async (e) => {
        e?.preventDefault();
        if (pin.length < 4) return;
        setBusy(true);
        try {
            const r = await axios.post(`${API}/api/research/share/${slug}/verify`, { pin });
            sessionStorage.setItem('research_share_slug', slug);
            sessionStorage.setItem('research_share_token', r.data.token);
            onSuccess();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'PIN hatalı');
            setPin('');
        } finally { setBusy(false); }
    };
    return (
        <div className="min-h-screen bg-gradient-to-br from-indigo-50 to-white flex items-center justify-center p-4">
            <form onSubmit={submit} className="w-full max-w-xs bg-white rounded-2xl shadow-xl p-6 text-center" data-testid="pin-gate">
                <div className="w-12 h-12 mx-auto bg-indigo-100 rounded-full flex items-center justify-center">
                    <Lock size={20} className="text-indigo-600" />
                </div>
                <h2 className="mt-3 text-lg font-bold text-gray-900">Araştırma Linki</h2>
                <p className="text-xs text-gray-500 mt-1">Devam etmek için PIN&apos;i girin</p>
                <input value={pin} onChange={(e) => setPin(e.target.value.replace(/\D/g, '').slice(0, 8))}
                    inputMode="numeric" autoFocus
                    className="mt-4 w-full h-14 text-center text-2xl font-mono tracking-widest border-2 border-gray-300 rounded-lg focus:border-indigo-500 outline-none"
                    placeholder="••••"
                    data-testid="pin-input" />
                <button type="submit" disabled={busy || pin.length < 4}
                    className="mt-3 w-full h-12 bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 text-white font-bold rounded-lg"
                    data-testid="pin-submit">
                    {busy ? <Loader2 size={18} className="animate-spin mx-auto" /> : 'Giriş'}
                </button>
            </form>
        </div>
    );
}

function AddProductModal({ onClose, onSaved }) {
    const [name, setName] = useState('');
    const [notes, setNotes] = useState('');
    const [saving, setSaving] = useState(false);
    const save = async () => {
        if (!name.trim()) { toast.error('Ürün adı gerekli'); return; }
        setSaving(true);
        try {
            await axios.post(`${API}/api/research/products`,
                { name, notes }, { headers: getHeaders() });
            toast.success('Ürün eklendi');
            onSaved(); onClose();
        } catch (e) { toast.error(e.response?.data?.detail || 'Hata'); }
        finally { setSaving(false); }
    };
    return (
        <Modal onClose={onClose} title="Yeni Ürün">
            <Field label="Ürün Adı *">
                <input value={name} onChange={(e) => setName(e.target.value)} autoFocus
                    className="w-full h-11 px-3 border border-gray-300 rounded-md text-sm focus:border-indigo-500 outline-none"
                    placeholder="Örn: Coffee Cup 240ml" data-testid="add-name" />
            </Field>
            <Field label="Notlar">
                <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm" data-testid="add-notes" />
            </Field>
            <button onClick={save} disabled={saving} className="w-full h-12 bg-indigo-600 hover:bg-indigo-700 text-white font-bold rounded-md disabled:opacity-50" data-testid="add-save">
                {saving ? <Loader2 size={18} className="animate-spin mx-auto" /> : 'Kaydet'}
            </button>
        </Modal>
    );
}

function ShareSettings({ onClose }) {
    const [cfg, setCfg] = useState(null);
    const [pin, setPin] = useState('');
    const [saving, setSaving] = useState(false);
    useEffect(() => {
        axios.get(`${API}/api/research/share-config`, { headers: getHeaders() })
            .then((r) => setCfg(r.data)).catch(() => {});
    }, []);
    const save = async () => {
        if (pin.length < 4) { toast.error('PIN min 4 rakam'); return; }
        setSaving(true);
        try {
            const r = await axios.post(`${API}/api/research/share-config`,
                { pin, active: true }, { headers: getHeaders() });
            setCfg({ ...cfg, slug: r.data.slug, url: r.data.url, has_pin: true, active: true });
            setPin('');
            toast.success('PIN kaydedildi');
        } catch (e) { toast.error(e.response?.data?.detail || 'Hata'); }
        finally { setSaving(false); }
    };
    const fullUrl = cfg?.slug ? `${window.location.origin}/r/${cfg.slug}` : '';
    const copy = () => { safeCopy(fullUrl, 'Link kopyalandı'); };
    return (
        <Modal onClose={onClose} title="Paylaşım Linki">
            {cfg?.slug ? (
                <>
                    <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-3 text-center" data-testid="share-link">
                        <div className="text-[10px] font-bold uppercase text-indigo-600 mb-1">Link</div>
                        <div className="font-mono text-sm text-indigo-900 break-all">{fullUrl}</div>
                        <button onClick={copy} className="mt-2 h-9 px-3 text-xs font-bold text-white bg-indigo-600 rounded inline-flex items-center gap-1" data-testid="share-copy">
                            <Copy size={12} /> Kopyala
                        </button>
                    </div>
                    <div className="text-xs text-gray-600 leading-relaxed bg-amber-50 border border-amber-200 rounded p-2">
                        <strong>Nasıl kullanılır?</strong> Linki ve PIN&apos;i araştırma yapan kişiye gönderin.
                        Linke girip PIN&apos;i yazarak <strong>tüm ürün ve tedarikçileri yönetebilir</strong>.
                    </div>
                </>
            ) : (
                <div className="text-xs text-gray-500">Önce PIN belirleyin.</div>
            )}
            <Field label={cfg?.has_pin ? 'PIN Değiştir' : 'PIN Belirle (min 4 rakam)'}>
                <input value={pin} onChange={(e) => setPin(e.target.value.replace(/\D/g, '').slice(0, 8))}
                    inputMode="numeric" placeholder="1234"
                    className="w-full h-11 px-3 border border-gray-300 rounded-md text-sm font-mono text-center text-lg tracking-widest"
                    data-testid="share-pin-input" />
            </Field>
            <button onClick={save} disabled={saving || pin.length < 4}
                className="w-full h-11 bg-indigo-600 hover:bg-indigo-700 text-white font-bold rounded-md disabled:opacity-50" data-testid="share-save">
                {saving ? <Loader2 size={18} className="animate-spin mx-auto" /> : (cfg?.has_pin ? 'PIN Güncelle' : 'PIN Belirle')}
            </button>
        </Modal>
    );
}

function Modal({ children, title, onClose }) {
    return (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-end sm:items-center justify-center" onClick={onClose}>
            <div className="bg-white w-full sm:max-w-md sm:rounded-2xl rounded-t-2xl max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
                <div className="sticky top-0 bg-white border-b px-4 py-3 flex items-center justify-between">
                    <h2 className="font-bold text-gray-900">{title}</h2>
                    <button onClick={onClose} className="p-1"><X size={18} /></button>
                </div>
                <div className="p-4 space-y-3">{children}</div>
            </div>
        </div>
    );
}

function Field({ label, children }) {
    return (
        <div>
            <label className="text-[10px] font-bold uppercase text-gray-500 tracking-wide block mb-1">{label}</label>
            {children}
        </div>
    );
}
